import React, { useState,useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Switch, message } from 'antd';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ClipLoader } from 'react-spinners';
import { useSelector } from 'react-redux';

const CreateCourse = ({ visible, handleClose, initialData = '', onSubmit }) => {
  const loading = useSelector((state) => state.Course.loading);
  const [previewImage, setPreviewImage] = useState(initialData?.thumbnail || '');

    useEffect(() => {
    if (initialData?.thumbnail) {
      setPreviewImage(initialData.thumbnail);
    }
  }, [initialData]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: initialData?.title || '',
      description: initialData?.description || '',
      status: typeof initialData?.status === 'boolean' ? initialData?.status : true,
      thumbnail: initialData?.thumbnail || '',
    },
    validationSchema: Yup.object({
      title: Yup.string().required('Title is required'),
      description: Yup.string().required('Description is required'),
      thumbnail: Yup.string().required('Thumbnail is required'),
    }),
    onSubmit: (values, { resetForm }) => {
      if (!initialData) onSubmit(values, onClose);
      else onSubmit(initialData?._id, values, onClose);
    },
  });

  const onClose = () => {
    formik.resetForm();
    setPreviewImage('');
    handleClose();
  };

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      message.error('Please upload a valid image');
      return;
    }
    const base64 = await getBase64(file);
    setPreviewImage(base64);
    formik.setFieldValue('thumbnail', base64);
  };

  return (
    <Modal
      title={initialData ? 'Edit Course' : 'Create New Course'}
      open={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      centered
      width={500}
       className="custom-modal-header p-0"
      maskClosable={false}
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="row g-3">
          {/* Title */}
          <div className="col-12">
            <label className="modal-form">
              Title <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control fs-7"
              placeholder="Enter course title"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
            />
            {formik.touched.title && formik.errors.title && (
              <span style={{ color: 'red' }}>{formik.errors.title}</span>
            )}
          </div>

          {/* Description */}
          <div className="col-12">
            <label className="modal-form">
              Description <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control fs-7"
              placeholder="Enter course description"
              name="description"
              rows={3}
              value={formik.values.description}
              onChange={formik.handleChange}
            />
            {formik.touched.description && formik.errors.description && (
              <span style={{ color: 'red' }}>{formik.errors.description}</span>
            )}
          </div>

          {/* Thumbnail */}
          <div className="col-12">
            <label className="modal-form">
              Thumbnail <span className="text-danger">*</span>
            </label>

            {/* Input box styled same as others */}
            <div className="form-control fs-7 p-2">
              <input
                type="file"
                accept="image/*"
                onChange={handleThumbnailChange}
                className="form-control border-0 p-0"
                style={{ boxShadow: 'none' }}
              />
            </div>

            {/* Validation error */}
            {formik.touched.thumbnail && formik.errors.thumbnail && (
              <span style={{ color: 'red' }}>{formik.errors.thumbnail}</span>
            )}

            {/* Small preview below */}
            {previewImage && (
              <div
                className="mt-2 text-center"
                style={{
                  display: 'flex',
                  justifyContent: 'start',
                  alignItems: 'center',
                }}
              >
                <img
                  src={previewImage}
                  alt="Thumbnail Preview"
                  style={{
                    width: '120px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}
                />
              </div>
            )}
          </div>

          {/* Status */}
          <div className="col-12 d-flex flex-column align-items-start justify-content-start">
            <label className="modal-form m-0">Status</label>
            <Switch
              checked={formik.values.status}
              onChange={(checked) => formik.setFieldValue('status', checked)}
              style={{
                backgroundColor: formik.values.status ? 'black' : '#ccc',
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer mt-3">
          <button type="button" className="btn btn-light btn-md" onClick={onClose}>
            Close
          </button>
          <button
            type="submit"
            className="btn btn-primary btn-md ms-3"
            disabled={loading}
            style={{ minWidth: '80px' }}
          >
            {loading ? (
              <ClipLoader size={16} color="white" />
            ) : initialData ? (
              'Update'
            ) : (
              'Add'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

CreateCourse.propTypes = {
  visible: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
};

export default CreateCourse;
